/** biome-ignore-all lint/a11y/useSemanticElements: sits inside a clickable row, where a nested <button> is invalid HTML */
import { useNavigate } from '@tanstack/react-router';
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { useOrgName } from '@/common/hooks/useOrgName';

interface ObjectiveLinkProps {
  children: ReactNode;
}

/**
 * Opens the objectives page.
 *
 * Deliberately without `?objectiveId`, which the objectives route reads as "open this one for
 * editing" — a reader following a finding wants to see the objective in context, not be dropped
 * into a form they did not ask for.
 *
 * Navigates manually instead of rendering a `Link`: a entry row is itself a `<button>`, and an
 * `<a>` inside a button is invalid HTML — the click would toggle the row rather than navigate. The
 * same idiom as a star icon inside a clickable list row: `role="button"` plus explicit key handling
 * keeps it reachable without nesting interactive content. Works unchanged outside a row, so one
 * component serves both the list and the detail.
 */
export const ObjectiveLink = ({ children }: ObjectiveLinkProps) => {
  const navigate = useNavigate();
  const org = useOrgName();

  const open = () => navigate({ to: '/org/$org/objectives', params: { org } });

  return (
    <span
      role="button"
      tabIndex={0}
      data-testid="agentic-x-objective-link"
      onClick={(event: MouseEvent) => {
        event.stopPropagation();
        open();
      }}
      onKeyDown={(event: KeyboardEvent) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        event.stopPropagation();
        open();
      }}
      className="underline decoration-dotted underline-offset-4 hover:text-text-brand-primary hover:decoration-solid"
    >
      {children}
    </span>
  );
};
